import { useMemo } from 'react';

import { useWatch } from '@kanda-libs/ks-component-ts';

import { useCurrentJob, useCurrentJobLead } from 'hooks';
import { FIELD_NAMES } from 'pages/Quote/constants';
import {
  DepositLimits,
  checkHasSecondLine,
  getDepositLimit,
} from 'pages/Quote/helpers';
import { useDebounce } from 'use-debounce';

import { CheckoutFinanceRate, FormattedRates } from './constants';
import { checkHasCredit, formatRates } from './helpers';

export interface RatesHook {
  allRates: FormattedRates | [];
  formattedRates: FormattedRates | [];
  bnplFormattedRates: FormattedRates | [];
  interest: string;
  depositAmount: string;
  min: string;
  max: string;
  depositLimits: DepositLimits;
  showDepositWarning: boolean;
  duration: string;
  selectedRate?: CheckoutFinanceRate;
  minDeposit: number;
  error?: string;
  hasCredit: boolean;
  isSecondLine: boolean;
}

function useRates(): RatesHook {
  // Get job
  const { job } = useCurrentJob();
  // Get lead
  const { lead } = useCurrentJobLead();

  const hasCredit = useMemo(() => checkHasCredit(job, lead), [job, lead]);

  const isSecondLine = checkHasSecondLine(job);

  const fields = useWatch({
    name: [
      FIELD_NAMES.loanAmount,
      FIELD_NAMES.depositAmount,
      FIELD_NAMES.interest,
      FIELD_NAMES.duration,
      FIELD_NAMES.provider,
      FIELD_NAMES.error,
    ],
  });

  const [fieldValues] = useDebounce(fields, 100);
  const [loan, depositAmount, interest, duration, provider, error] = [
    ...fieldValues,
  ];

  const formattedRates = useMemo(
    () => formatRates(job, lead, loan, depositAmount),
    [job, lead, loan, depositAmount],
  );

  const bnplFormattedRates = useMemo(
    () => formatRates(job, lead, loan, depositAmount, true),
    [job, lead, loan, depositAmount],
  );

  const allRates: FormattedRates | [] = useMemo(
    () => ({
      ...formattedRates,
      ...bnplFormattedRates,
    }),
    [formattedRates, bnplFormattedRates],
  );

  const ratesArray =
    (formattedRates.length === 0 ? [] : allRates[interest]) || [];

  const selectedRate: CheckoutFinanceRate | undefined = ratesArray.filter(
    (rate: CheckoutFinanceRate) =>
      rate.duration === duration && rate.provider === provider,
  )?.[0];

  const depositLimits = useMemo(
    () => getDepositLimit(selectedRate?.name, job, lead),
    [selectedRate?.name, job, lead],
  );

  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;

  const minDeposit = parseInt(depositLimits.min, 10);
  const maxDeposit = parseInt(depositLimits.max, 10);

  // Slider variables
  const min = String(jobTotal - maxDeposit);
  const max = String(jobTotal - minDeposit);

  const showDepositWarning = useMemo(
    () => parseInt(max, 10) < jobTotal,
    [max, jobTotal],
  );

  return {
    allRates,
    formattedRates,
    bnplFormattedRates,
    interest,
    depositAmount,
    min,
    max,
    depositLimits,
    showDepositWarning,
    duration,
    selectedRate,
    minDeposit,
    error,
    hasCredit,
    isSecondLine,
  };
}

export default useRates;
